import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";
import MapPinIcon from "@mui/icons-material/LocationOn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Link } from "react-router-dom";
import integrationService, {
  INTEGRATION_CONFIG_REQUIRED_MESSAGE,
  INTEGRATION_NOT_CONFIGURED_CODE,
} from "../../../../services/integrationService";
import UrbanoLogo from "../../../../layouts/integrations/images/Urbano.svg";

const ModalPrice = ({ onClose, product_ids, onConfirm, datosCliente, packages = [] }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState(null);

  const providerLogos = { urbano: UrbanoLogo };

  useEffect(() => {
    const fetchQuotes = async () => {
      if (!product_ids || product_ids.length === 0) return;

      const postalCode = datosCliente?.cp;
      if (!String(postalCode ?? "").trim()) {
        setError("Ingresá el código postal del destinatario para cotizar el envío.");
        setErrorType("generic");
        return;
      }

      try {
        setLoading(true);
        setError("");
        setErrorType(null);
        const quotes = await integrationService.getDeliberyQuotes(
          product_ids,
          postalCode,
          packages
        );
        if (!quotes || quotes.length === 0) {
          setError("No hay cotizaciones disponibles para los productos seleccionados.");
          setErrorType("empty");
        } else {
          setShippingOptions(quotes);
        }
      } catch (err) {
        if (err?.code === INTEGRATION_NOT_CONFIGURED_CODE) {
          setError(INTEGRATION_CONFIG_REQUIRED_MESSAGE);
          setErrorType("integration");
        } else {
          setError(
            err?.message ||
              "No se pudieron obtener las cotizaciones de envío. Intentá nuevamente."
          );
          setErrorType("generic");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, [product_ids, datosCliente?.cp]);

  const handleConfirm = () => {
    if (selectedIndex !== null) onConfirm(shippingOptions[selectedIndex]);
  };

  return (
    <Box className="space-y-6">
      <Typography variant="h6" fontWeight="bold" textAlign={"start"} mb={2}>
        2. Selección de Paquetería
      </Typography>

      <Box
        sx={{
          p: 2,
          bgcolor: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <MapPinIcon sx={{ color: "#16a34a" }} fontSize="small" />
        <Typography variant="body2" sx={{ color: "#166534" }}>
          Dirección validada para{" "}
          <b>
            {datosCliente?.localidad}, {datosCliente?.cp}
          </b>
          .
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              ...(errorType === "integration"
                ? {
                    bgcolor: "#fffbeb",
                    border: "1px solid #fcd34d",
                  }
                : {
                    bgcolor: "#fef2f2",
                    border: "1px solid #fecaca",
                  }),
            }}
          >
            {errorType === "integration" ? (
              <SettingsOutlinedIcon
                sx={{ color: "#d97706", mt: 0.25, flexShrink: 0 }}
                fontSize="small"
              />
            ) : (
              <ErrorOutlineIcon
                sx={{ color: "#dc2626", mt: 0.25, flexShrink: 0 }}
                fontSize="small"
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  color: errorType === "integration" ? "#92400e" : "#991b1b",
                  mb: 0.5,
                }}
              >
                {errorType === "integration"
                  ? ""
                  : "No se pudieron cargar las cotizaciones"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: errorType === "integration" ? "#78350f" : "#b91c1c" }}
              >
                {error}
              </Typography>
              {errorType === "integration" && (
                <Button
                  component={Link}
                  to="/integrations"
                  size="small"
                  sx={{
                    mt: 1.5,
                    px: 0,
                    minWidth: 0,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#b45309",
                    "&:hover": { bgcolor: "transparent", color: "#92400e" },
                  }}
                >
                  Ir a configurar integraciones
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          shippingOptions.map((option, index) => {
            const isSelected = selectedIndex === index;
            return (
              <Box
                key={index}
                onClick={() => setSelectedIndex(index)}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  borderRadius: "12px",
                  border: isSelected
                    ? "2px solid #3b82f6"
                    : "1px solid #e2e8f0",
                  bgcolor: isSelected ? "#eff6ff" : "white",
                  boxShadow: isSelected
                    ? "0 0 0 4px rgba(191, 219, 254, 0.5)"
                    : "none",
                  "&:hover": { bgcolor: isSelected ? "#eff6ff" : "#f8fafc" },
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Paper
                    variant="outlined"
                    sx={{
                      width: 50,
                      height: 35,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                      bgcolor: "white",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={
                        providerLogos[option.provider_name.toLowerCase()] || ""
                      }
                      alt="logo"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Paper>

                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ color: "#1e293b" }}
                      textAlign={"left"}
                    >
                      {option.provider_name.toUpperCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Tiempo estimado: {option.time_to_delibery} días
                    </Typography>
                  </Box>
                </Box>

                <Box textAlign="right">
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#2563eb" }}
                  >
                    $ {option.price.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      fontWeight: "bold",
                      letterSpacing: 1,
                    }}
                  >
                    COSTO DE ENVÍO
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderColor: "#cbd5e1",
            color: "#475569",
            "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
          }}
        >
          Volver
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedIndex === null}
          endIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
};

export default ModalPrice;
